import * as MenuPrimitive from '@radix-ui/react-dropdown-menu'
import * as React from 'react'

import { cn } from '../../lib/utils'

interface DropdownMenuProps {
  /** The element that opens the menu. Rendered as the trigger itself. */
  trigger: React.ReactElement
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
}

function DropdownMenu({
  trigger,
  children,
  align = 'end',
  side = 'bottom',
  className,
}: DropdownMenuProps) {
  return (
    <MenuPrimitive.Root>
      <MenuPrimitive.Trigger asChild>{trigger}</MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          data-slot="dropdown-menu"
          align={align}
          side={side}
          sideOffset={4}
          className={cn(
            'border-border bg-bg-elev shadow-pop z-40 min-w-48 rounded-lg border p-1 outline-none',
            'origin-(--radix-dropdown-menu-content-transform-origin) duration-100 ease-out',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className,
          )}
        >
          {children}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  )
}

function DropdownMenuItem({
  className,
  destructive,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.Item> & { destructive?: boolean }) {
  return (
    <MenuPrimitive.Item
      className={cn(
        'flex w-full cursor-default items-center gap-2 rounded-sm px-2.5 py-2 text-left text-base outline-none select-none',
        destructive
          ? 'text-err data-[highlighted]:bg-err-surface'
          : 'text-fg data-[highlighted]:bg-bg-subtle',
        '[&_svg]:text-fg-subtle [&_svg]:size-3.5 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({ className }: { className?: string }) {
  return <MenuPrimitive.Separator className={cn('bg-border my-1 h-px', className)} />
}

function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentProps<typeof MenuPrimitive.Label>) {
  return <MenuPrimitive.Label className={cn('t-label px-2 py-1', className)} {...props} />
}

export {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  type DropdownMenuProps,
}
